import { useEffect, useSyncExternalStore } from "react";
// Custom components
import { snackbar } from "components/awesome_snackbar/snackbar";
// API calls
import db from "db/offlineDb";
import {
  deleteQueuedSubmission,
  flushAttendanceQueue,
  getPendingCount,
  getQueuedSubmissions,
} from "db/syncAttendance";
// Context
import { profile } from "context/profile";

// Gives the connection a moment to settle before syncing after it comes back
const ONLINE_SYNC_DELAY_MS = 5000;
// Prefix of submissions saved by the old localStorage queue
const LEGACY_QUEUE_PREFIX = "attendanceData_";
// First wait before retrying a queue the server could not take, doubled after each failed attempt
const RETRY_BASE_DELAY_MS = 30 * 1000;
const RETRY_MAX_DELAY_MS = 5 * 60 * 1000;

// Shared across every hook instance so the app has a single sync state and a single online listener
let syncState = { submissions: [], pendingCount: 0, isSyncing: false, hasFailedSync: false };
const listeners = new Set();
let onlineSyncTimer = null;
let retryTimer = null;
let retryDelay = RETRY_BASE_DELAY_MS;
// Assigned below, once the sync it points to is defined
let runScheduledSync = () => {};

const setSyncState = (changes) => {
  syncState = { ...syncState, ...changes };
  listeners.forEach((listener) => listener());
};

const getSyncState = () => syncState;

const clearRetry = () => {
  clearTimeout(retryTimer);
  retryTimer = null;
};

// Retries the queue later, since a failed request while "online" fires no online event
const scheduleRetry = () => {
  if (syncState.pendingCount === 0) {
    retryDelay = RETRY_BASE_DELAY_MS;
    clearRetry();
    // An empty queue has nothing left to fail
    if (syncState.hasFailedSync) setSyncState({ hasFailedSync: false });
    return;
  }
  if (retryTimer || syncState.isSyncing || listeners.size === 0) return;

  retryTimer = setTimeout(() => {
    retryTimer = null;
    // The online event takes over once the connection returns
    if (navigator.onLine) runScheduledSync();
  }, retryDelay);
};

// Reads the queued submissions into the shared state
const refreshQueue = async () => {
  try {
    const submissions = await getQueuedSubmissions();
    setSyncState({ submissions, pendingCount: submissions.length });
  } catch {
    setSyncState({ submissions: [], pendingCount: 0 });
  }
  scheduleRetry();
};

// Moves submissions from the old localStorage queue into IndexedDB so one queue syncs them all
const migrateLegacyQueue = async (staffId) => {
  const keys = Object.keys(localStorage).filter((key) =>
    key.startsWith(`${LEGACY_QUEUE_PREFIX}${staffId}`),
  );

  for (const key of keys) {
    try {
      const records = JSON.parse(localStorage.getItem(key));
      if (Array.isArray(records) && records.length > 0) {
        const payload = records.map((record) => ({
          ...record,
          date:
            typeof record.date === "string"
              ? record.date
              : new Date(record.date).toISOString().split("T")[0],
        }));
        await db.attendanceQueue.add({
          payload,
          date: payload[0].date,
          class: key.slice(`${LEGACY_QUEUE_PREFIX}${staffId}_`.length) || "All",
          status: "pending",
          createdAt: Date.now(),
        });
      }
      localStorage.removeItem(key);
    } catch {
      // Unreadable entries can never be sent
      localStorage.removeItem(key);
    }
  }
};

// Sends queued attendance to the server. Manual syncs also report when nothing could be sent.
const syncPendingAttendance = async ({ manual = false } = {}) => {
  if (!profile?.staff_id || syncState.isSyncing) return;
  // Automatic syncs wait for the online event instead of failing while offline
  if (!manual && !navigator.onLine) return;

  clearRetry();
  setSyncState({ isSyncing: true });
  let hasFailedSync = false;
  try {
    await migrateLegacyQueue(profile.staff_id);
    if ((await getPendingCount()) === 0) return;
    // Lists any submissions just moved over from the legacy queue
    await refreshQueue();

    const synced = await flushAttendanceQueue();
    const remaining = await getPendingCount();
    hasFailedSync = remaining > 0;
    // Back off only while nothing gets through
    retryDelay = synced > 0 ? RETRY_BASE_DELAY_MS : Math.min(retryDelay * 2, RETRY_MAX_DELAY_MS);
    if (synced > 0) snackbar("success", `${synced} pending attendance submission(s) synced.`);
    if (manual && remaining > 0) {
      snackbar(
        "warning",
        `${remaining} submission(s) could not be synced. They will be retried automatically.`,
      );
    }
  } catch {
    hasFailedSync = true;
    retryDelay = Math.min(retryDelay * 2, RETRY_MAX_DELAY_MS);
    if (manual) snackbar("error", "Failed to sync pending attendance.");
  } finally {
    setSyncState({ isSyncing: false, hasFailedSync });
    await refreshQueue();
  }
};

runScheduledSync = () => syncPendingAttendance();

// Deletes a queued submission that has not been sent. Returns whether it was deleted.
const deleteSubmission = async (id) => {
  if (syncState.isSyncing) return false;
  try {
    return await deleteQueuedSubmission(id);
  } catch {
    return false;
  } finally {
    await refreshQueue();
  }
};

const handleOnline = () => {
  // A fresh connection deserves a fresh retry schedule
  clearRetry();
  retryDelay = RETRY_BASE_DELAY_MS;
  clearTimeout(onlineSyncTimer);
  onlineSyncTimer = setTimeout(() => syncPendingAttendance(), ONLINE_SYNC_DELAY_MS);
};

const subscribe = (listener) => {
  if (listeners.size === 0) window.addEventListener("online", handleOnline);
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener("online", handleOnline);
      clearTimeout(onlineSyncTimer);
      clearRetry();
    }
  };
};

/**
 * useAttendanceSync is the single owner of the offline attendance queue. It syncs queued
 * submissions when the connection comes back, retries them with a growing delay while the
 * server cannot be reached, and shares the pending count, syncing state and queued submissions
 * with every component that uses it.
 * @returns {{
 *   submissions: object[],
 *   pendingCount: number,
 *   isSyncing: boolean,
 *   hasFailedSync: boolean,
 *   syncNow: function,
 *   syncPendingAttendance: function,
 *   refreshQueue: function,
 *   deleteSubmission: function,
 * }} The queued submissions, the shared sync state, whether the last sync left submissions
 * unsent, a manual sync that reports its outcome, a silent sync, a function that re-reads the
 * queue, and one that deletes an unsent submission
 * @example
 * const { pendingCount, isSyncing, syncNow } = useAttendanceSync();
 */
export function useAttendanceSync() {
  const { submissions, pendingCount, isSyncing, hasFailedSync } = useSyncExternalStore(
    subscribe,
    getSyncState,
  );

  useEffect(() => {
    refreshQueue();
  }, []);

  return {
    submissions,
    pendingCount,
    isSyncing,
    hasFailedSync,
    syncNow: () => syncPendingAttendance({ manual: true }),
    syncPendingAttendance,
    refreshQueue,
    deleteSubmission,
  };
}

export default useAttendanceSync;
